/**
 * Наставник миссий — живёт поверх любого раздела.
 *
 * Пока миссия идёт, в углу экрана карточка: что сделать и кнопка «Где
 * это?». Миссия засчитывается сама, как только дело сделано, — тогда
 * вспыхивает «Миссия выполнена», опыт и, если набрался, новый уровень.
 *
 * При первом открытии после входа уже сделанное засчитывается молча:
 * у того, кто давно собрал ботов, не должно посыпаться десять поздравлений.
 */

import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useTerminal } from "../state/store.js";
import { NeonIcon } from "../ui/NeonIcon.js";
import {
  levelOf,
  missionById,
  missionsFor,
  MISSIONS,
  titleOf,
  xpOf,
  type Mission,
} from "./missions.js";

/** Подсказка от графика: «эта свеча обычная» или «попали». */
export interface MissionNote {
  readonly ok: boolean;
  readonly text: string;
}

export const MISSION_NOTE_EVENT = "anzonix:mission-note";
export const OPEN_BOTS_EVENT = "anzonix:open-bots";

/** Отвести к миссии: раздел и нужная панель. */
export function goToMission(mission: Mission): void {
  const state = useTerminal.getState();
  window.location.hash = mission.go.section === "radar" ? "#/radar" : "#/chart";
  if (mission.go.open === "studio") state.openStudio(null);
  // Панель ботов открывает себя сама по событию: её состояние живёт в ней.
  if (mission.go.open === "bots")
    window.setTimeout(
      () => window.dispatchEvent(new Event(OPEN_BOTS_EVENT)),
      300,
    );
}

export function startMission(mission: Mission): void {
  useTerminal.getState().patchJourney({ active: mission.id });
  goToMission(mission);
}

export function MissionCoach(): JSX.Element | null {
  const loaded = useTerminal((s) => s.syncState !== "loading");
  const welcomed = useTerminal((s) => s.journey.welcomed);
  const active = useTerminal((s) => s.journey.active);
  const [note, setNote] = useState<MissionNote | null>(null);
  const [done, setDone] = useState<{
    mission: Mission;
    level: number | null;
  } | null>(null);
  const primed = useRef(false);

  // Миссии засчитываются по состоянию — любым путём, каким бы человек ни сделал дело.
  useEffect(() => {
    if (!loaded) return;
    const check = (): void => {
      const state = useTerminal.getState();
      const fresh = MISSIONS.filter(
        (mission) =>
          mission.id !== "move" &&
          !state.journey.missions.some((mark) => mark.id === mission.id) &&
          mission.done(state),
      );
      if (fresh.length === 0) return;
      const before = levelOf(xpOf(state)).level;
      for (const mission of fresh) state.completeMission(mission.id);
      if (!primed.current) return;
      const after = levelOf(xpOf(useTerminal.getState())).level;
      setDone({
        mission: fresh[fresh.length - 1]!,
        level: after > before ? after : null,
      });
    };
    check();
    primed.current = true;
    return useTerminal.subscribe(check);
  }, [loaded]);

  // Подсказки графика к миссии «найди сильное движение».
  useEffect(() => {
    const onNote = (event: Event): void => {
      const detail = (
        event as CustomEvent<MissionNote & { level?: number | null }>
      ).detail;
      if (detail.ok) {
        setNote(null);
        setDone({ mission: missionById("move")!, level: detail.level ?? null });
      } else setNote(detail);
    };
    window.addEventListener(MISSION_NOTE_EVENT, onNote);
    return () => window.removeEventListener(MISSION_NOTE_EVENT, onNote);
  }, []);

  useEffect(() => setNote(null), [active]);

  if (!loaded || !welcomed) return null;

  if (done) {
    const path = useTerminal.getState().journey.path;
    const next = missionsFor(path).find(
      (mission) => !mission.done(useTerminal.getState()),
    );
    const xp = xpOf(useTerminal.getState());
    const { level } = levelOf(xp);
    return (
      <>
        <div className="mc-scrim" onClick={() => setDone(null)} />
        <div
          className="mc-done"
          role="status"
          style={{ ["--tone" as string]: done.mission.tone }}
        >
          <span className="mc-done-burst" aria-hidden="true" />
          <p className="mc-done-kicker">Миссия выполнена</p>
          <h3>{done.mission.title}</h3>
          <p className="mc-done-xp">+{done.mission.xp} опыта</p>
          {done.level !== null && (
            <p className="mc-done-level">
              Новый уровень: <b>{level}</b> · {titleOf(level)}
            </p>
          )}
          <p className="mc-done-teach">{done.mission.teach}</p>
          <div className="mc-done-actions">
            {next && (
              <button
                type="button"
                className="mc-btn mc-btn--primary"
                onClick={() => {
                  setDone(null);
                  startMission(next);
                }}
              >
                Дальше: {next.title}
              </button>
            )}
            <button
              type="button"
              className="mc-btn"
              onClick={() => setDone(null)}
            >
              {next ? "Позже" : "Отлично"}
            </button>
          </div>
        </div>
      </>
    );
  }

  const mission = missionById(active);
  if (!mission || mission.done(useTerminal.getState())) return null;
  return (
    <aside
      className="mc"
      style={{ ["--tone" as string]: mission.tone }}
      aria-label="Текущая миссия"
    >
      <span className="mc-icon" aria-hidden="true">
        <NeonIcon name={mission.icon} size={24} />
      </span>
      <div className="mc-body">
        <p className="mc-kicker">Миссия · +{mission.xp} опыта</p>
        <b>{mission.title}</b>
        <p>{mission.how}</p>
        {note && (
          <p className={note.ok ? "mc-note mc-note--ok" : "mc-note"}>
            {note.text}
          </p>
        )}
        <div className="mc-actions">
          <button
            type="button"
            className="mc-btn mc-btn--primary"
            onClick={() => goToMission(mission)}
          >
            Где это?
          </button>
          <button
            type="button"
            className="mc-btn"
            onClick={() =>
              useTerminal.getState().patchJourney({ active: null })
            }
          >
            Скрыть
          </button>
        </div>
      </div>
    </aside>
  );
}
